import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { fmtINR, fmtDate } from "../lib/format";
import { AlertTriangle } from "lucide-react";

export default function MissedRenewals() {
  const [data, setData] = useState({ items: [], total: 0, avg_days_late: 0, total_amount: 0 });
  const [month, setMonth] = useState("");
  const [responsible, setResponsible] = useState("");

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (month) params.set("month", month);
    if (responsible) params.set("responsible", responsible);
    const { data } = await api.get(`/missed-renewals?${params}`);
    setData(data);
  }, [month, responsible]);
  useEffect(() => { load(); }, [load]);

  const responsibles = [...new Set(data.items.map((i) => i.responsible_name).filter(Boolean))];
  const thisMonth = new Date().toISOString().slice(0, 7);
  const lastMonth = new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().slice(0, 7);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-head text-2xl font-extrabold flex items-center gap-2"><AlertTriangle className="text-red-600" /> Missed / Late Renewals</h1>
        <p className="text-slate-500 text-sm mt-1">Permanent record of every recharge completed after expiry. This history is never deleted.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border rounded-xl p-4"><div className="text-xs text-slate-500 uppercase font-bold">Total Late</div><div className="font-head text-3xl font-extrabold text-red-600">{data.total}</div></div>
        <div className="bg-white border rounded-xl p-4"><div className="text-xs text-slate-500 uppercase font-bold">Avg Days Late</div><div className="font-head text-3xl font-extrabold">{data.avg_days_late}</div></div>
        <div className="bg-white border rounded-xl p-4"><div className="text-xs text-slate-500 uppercase font-bold">Amount Involved</div><div className="font-head text-2xl font-extrabold font-mono-num">{fmtINR(data.total_amount)}</div></div>
        <div className="bg-white border rounded-xl p-4"><div className="text-xs text-slate-500 uppercase font-bold">Employees</div><div className="font-head text-3xl font-extrabold">{responsibles.length}</div></div>
      </div>

      <div className="flex flex-wrap gap-2">
        <select value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 px-3 rounded-lg border text-sm" data-testid="mr-month">
          <option value="">All months</option><option value={thisMonth}>This month</option><option value={lastMonth}>Last month</option>
        </select>
        <select value={responsible} onChange={(e) => setResponsible(e.target.value)} className="h-9 px-3 rounded-lg border text-sm" data-testid="mr-responsible">
          <option value="">All employees</option>{responsibles.map((r) => <option key={r}>{r}</option>)}
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 border-b text-left text-[11px] uppercase text-slate-500">
            <th className="px-4 py-3">Customer</th><th className="px-4 py-3">Circuit</th><th className="px-4 py-3">ISP</th>
            <th className="px-4 py-3">Expiry</th><th className="px-4 py-3">Recharged</th><th className="px-4 py-3">Days Late</th>
            <th className="px-4 py-3 text-right">Amount</th><th className="px-4 py-3">Responsible</th><th className="px-4 py-3">Reason</th></tr></thead>
          <tbody>
            {data.items.map((m) => (
              <tr key={m.id} className="border-b border-slate-100">
                <td className="px-4 py-3 font-semibold">{m.customer_name}</td>
                <td className="px-4 py-3 font-mono-num text-xs">{m.internal_circuit_id}</td>
                <td className="px-4 py-3">{m.vendor_name}</td>
                <td className="px-4 py-3 font-mono-num text-xs">{fmtDate(m.expiry_date)}</td>
                <td className="px-4 py-3 font-mono-num text-xs">{fmtDate(m.recharge_date)}</td>
                <td className="px-4 py-3"><span className="text-xs font-bold px-2 py-0.5 rounded bg-red-100 text-red-700">{m.days_late}d</span></td>
                <td className="px-4 py-3 text-right font-mono-num">{fmtINR(m.amount)}</td>
                <td className="px-4 py-3">{m.responsible_name || "—"}</td>
                <td className="px-4 py-3 text-slate-500 text-xs">{m.reason_for_delay || "—"}</td>
              </tr>
            ))}
            {data.items.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">No late renewals recorded.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
